import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useTags, useTagCategories } from '../../graphql/queries';
import { deleteTag, deleteTagCategory, ApiError } from '../../lib/apiClient';
import { usePermission } from '../../hooks/usePermission';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/features/auth/authSlice';
import { canManageTagCategory } from '../../lib/permissions';
import { resolveTagCategoryName } from '../../lib/tagCategoryName';
import { TagBadge } from '../../components/tags';
import { Pagination } from '../../components/shared';
import type { Tag, TagFilter } from '../../graphql/types';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/tags/')({
  component: TagsListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
    categoryId: typeof search.categoryId === 'string' ? search.categoryId : '',
  }),
});

export { TagsListPage };

/**
 * Cursor cache mapping page number -> the `after` cursor to fetch that page.
 * Page 1 always maps to `undefined` (no cursor). Cursors for later pages are
 * discovered as the user pages forward (issue #499 — the `tags` GraphQL query
 * only supports forward `first`/`after` cursors, not offset/`before`, so a
 * page not yet visited cannot be jumped to directly).
 */
interface CursorCache {
  key: string;
  cursors: Record<number, string | undefined>;
}

function TagsListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize, categoryId } = Route.useSearch();
  const canManageTags = usePermission('tag:create');
  const canManageCategories = usePermission('tagCategory:create');
  const currentUser = useAppSelector(selectUser);

  const [deleteError, setDeleteError] = useState<string | null>(null);

  const {
    data: categoriesData,
    loading: categoriesLoading,
    refetch: refetchCategories,
  } = useTagCategories();
  const categories = categoriesData?.tagCategories ?? [];

  // The filter is always scoped to exactly one category (#715 — "All
  // Categories" was removed because it fetched only page 1 unfiltered,
  // making every category past the first page look empty). Default to the
  // first category in the list when the URL has none selected yet.
  const defaultCategoryId = categories.length > 0 ? categories[0].entityId : '';
  const effectiveCategoryId = categoryId || defaultCategoryId;

  // Cursor cache is keyed by filter + page size — any change invalidates
  // previously discovered cursors, so reset to page 1 with no cursor.
  const cacheKey = `${effectiveCategoryId}::${pageSize}`;
  const [cursorCache, setCursorCache] = useState<CursorCache>({
    key: cacheKey,
    cursors: { 1: undefined },
  });

  // Adjust state during render (React-recommended pattern for resetting
  // derived state on a prop/search-param change) rather than an Effect, so
  // the very next fetch already uses the reset cursor — no stale-page flash.
  const effectiveCursors = cursorCache.key === cacheKey ? cursorCache.cursors : { 1: undefined };
  if (cursorCache.key !== cacheKey) {
    setCursorCache({ key: cacheKey, cursors: { 1: undefined } });
  }

  const tagFilter: TagFilter | undefined = effectiveCategoryId
    ? { categoryId: effectiveCategoryId }
    : undefined;
  // page is a validated positive integer from Route.validateSearch (Math.max(1, Number(...))),
  // never a user-supplied string key, so this dynamic lookup carries no prototype-pollution risk.
  // eslint-disable-next-line security/detect-object-injection
  const after = effectiveCursors[page];

  // Skip the tags fetch until we know which single category to scope it to:
  // while categories are still loading (we don't yet know the default), and
  // when no categories exist at all (there is no category to scope to, and
  // therefore nothing to scope-safely fetch). Without this guard the fetch
  // would briefly fall through to an unfiltered, page-1-only request.
  const skipTagsQuery = categoriesLoading || categories.length === 0;
  const {
    data,
    loading: tagsLoading,
    error,
    refetch,
  } = useTags(tagFilter, pageSize, after, skipTagsQuery);
  const loading = categoriesLoading || tagsLoading;
  const tags = data?.tags?.edges?.map((e) => e.node) ?? [];
  const totalCount = data?.tags?.totalCount ?? tags.length;
  const pageInfo = data?.tags?.pageInfo;

  // Cache the cursor for the *next* page once this page's fetch resolves.
  if (
    !loading &&
    pageInfo?.hasNextPage &&
    pageInfo.endCursor &&
    cursorCache.key === cacheKey &&
    !(page + 1 in cursorCache.cursors)
  ) {
    setCursorCache((prev) => ({
      ...prev,
      cursors: { ...prev.cursors, [page + 1]: pageInfo.endCursor ?? undefined },
    }));
  }

  const handleCategoryFilterChange = (newCategoryId: string): void => {
    void navigate({ to: '/tags', search: { page: 1, pageSize, categoryId: newCategoryId } });
  };

  const handlePageChange = (requestedPage: number): void => {
    const knownPages = Object.keys(effectiveCursors).map(Number);
    const maxKnownPage = Math.max(1, ...knownPages);
    const targetPage = Math.max(1, Math.min(requestedPage, maxKnownPage + 1));
    void navigate({ to: '/tags', search: { page: targetPage, pageSize, categoryId } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({ to: '/tags', search: { page: 1, pageSize: newPageSize, categoryId } });
  };

  const ForgeButton = 'forge-button' as React.ElementType;
  const ForgeIcon = 'forge-icon' as React.ElementType;

  const handleRowClick = (tag: Tag): void => {
    void navigate({ to: '/tags/$tagId/edit', params: { tagId: tag.entityId } });
  };

  const handleDelete = async (e: React.MouseEvent, tagId: string): Promise<void> => {
    e.stopPropagation();
    if (!window.confirm('Delete this tag?')) return;
    try {
      await deleteTag(tagId);
      void refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        setDeleteError(err.message);
      } else {
        setDeleteError('Failed to delete tag');
      }
    }
  };

  const handleDeleteCategory = async (
    e: React.MouseEvent,
    tagCategoryId: string
  ): Promise<void> => {
    e.stopPropagation();
    if (
      !window.confirm('Delete this tag category? All tags in this category will also be removed.')
    )
      return;
    try {
      await deleteTagCategory(tagCategoryId);
      void refetchCategories();
    } catch (err) {
      if (err instanceof ApiError) {
        setDeleteError(err.message);
      } else {
        setDeleteError('Failed to delete category');
      }
    }
  };

  return (
    <div className="tags-list-page">
      {/* ── Tag Categories Section ── */}
      <div className="page-header page-header--toolbar page-header--center page-header--spaced">
        <h2 className="forge-typography--headline5">Tag Categories</h2>
        {canManageCategories && (
          <Link to="/tags/categories/new" className="create-tag-category-link">
            <ForgeButton variant="raised">
              <ForgeIcon name="add" slot="start" />
              Create Category
            </ForgeButton>
          </Link>
        )}
      </div>

      {deleteError && (
        <p className="tags-list-page__error" role="alert">
          {deleteError}
        </p>
      )}

      {categoriesLoading && <p>Loading categories...</p>}

      {!categoriesLoading && categories.length === 0 && (
        <p className="tags-list-page__empty">
          No tag categories found.
          {canManageCategories
            ? ' Create a category to get started.'
            : ' Ask an Administrator or team primary contact to create categories.'}
        </p>
      )}

      {!categoriesLoading && categories.length > 0 && (
        <table className="tags-table tags-table--categories">
          <thead>
            <tr>
              <th>Name</th>
              <th>Scope</th>
              <th>Allow Multiple</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => {
              const canEdit = canManageTagCategory(currentUser, {
                scope: cat.scope,
                scopeOwnerId: cat.scopeOwnerId ?? undefined,
              });
              return (
                <tr key={cat.entityId} className="tags-table__row">
                  <td>{cat.name}</td>
                  <td>{cat.scope === 'global' ? 'Global' : `Team: ${cat.scopeOwnerId ?? ''}`}</td>
                  <td>{cat.allowMultiple ? 'Yes' : 'No'}</td>
                  <td>{cat.status}</td>
                  <td className="tags-table__actions">
                    {canEdit && (
                      <>
                        <Link
                          to="/tags/categories/$categoryId/edit"
                          params={{ categoryId: cat.entityId }}
                        >
                          <ForgeButton variant="outlined">Edit</ForgeButton>
                        </Link>
                        <ForgeButton
                          variant="outlined"
                          className="action-btn--danger"
                          onClick={(e: React.MouseEvent) =>
                            void handleDeleteCategory(e, cat.entityId)
                          }
                        >
                          Delete
                        </ForgeButton>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ── Tags Section ── */}
      <div className="page-header page-header--toolbar page-header--center page-header--spaced page-header--gap-top">
        <h2 className="forge-typography--headline5">Tags</h2>
        {canManageTags && (
          <Link to="/tags/new" className="create-tag-link">
            <ForgeButton variant="raised">
              <ForgeIcon name="add" slot="start" />
              Create Tag
            </ForgeButton>
          </Link>
        )}
      </div>

      {!categoriesLoading && categories.length > 0 && (
        <div className="filters-row">
          <div className="filter-selects">
            <select
              className="filter-select"
              aria-label="Filter by tag category"
              value={effectiveCategoryId}
              onChange={(e) => handleCategoryFilterChange(e.target.value)}
            >
              {categories.map((cat) => (
                <option key={cat.entityId} value={cat.entityId}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {loading && <p>Loading tags...</p>}

      {error && !loading && <p className="tags-list-page__error">Failed to load tags</p>}

      {!loading && !error && tags.length === 0 && (
        <p className="tags-list-page__empty">
          {effectiveCategoryId ? 'No tags found in this category' : 'No tags found'}
        </p>
      )}

      {!loading && !error && tags.length > 0 && (
        <table className="tags-table tags-table--items">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Color</th>
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tags.map((tag) => {
              const tagCategory = categories.find((c) => c.entityId === tag.categoryId);
              const canEditTag = tagCategory
                ? canManageTagCategory(currentUser, {
                    scope: tagCategory.scope,
                    scopeOwnerId: tagCategory.scopeOwnerId ?? undefined,
                  })
                : false;
              return (
                <tr
                  key={tag.entityId}
                  className="tags-table__row"
                  onClick={() => handleRowClick(tag)}
                  style={{ cursor: canEditTag ? 'pointer' : 'default' }}
                >
                  <td>
                    <TagBadge
                      tag={tag}
                      categoryName={resolveTagCategoryName(tag.categoryId, categories)}
                    />
                  </td>
                  <td>{tagCategory?.name ?? tag.categoryId}</td>
                  <td>{tag.color ?? '—'}</td>
                  <td>{tag.description ?? '—'}</td>
                  <td>{tag.status}</td>
                  <td>
                    {canEditTag && (
                      <ForgeButton
                        variant="outlined"
                        onClick={(e: React.MouseEvent) => void handleDelete(e, tag.entityId)}
                      >
                        Delete
                      </ForgeButton>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!loading && !error && (
        <Pagination
          totalCount={totalCount}
          pageSize={pageSize}
          currentPage={page}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
}
